/**
 * The camera screen's pinch zoom, as arithmetic.
 *
 * The zoom itself is the camera's, not CSS: `zoom` is a MediaStreamTrack
 * constraint, so what is enlarged is the track — and the track is what a
 * photograph is drawn from and what a clip and a кружочек record. A transform
 * on the element would zoom the viewfinder and film the wide shot, which is a
 * lie no framing survives; so where the track cannot zoom, there is no pinch,
 * the same way there is no flash button over a camera with no torch.
 *
 * Everything stateful — the fingers, the throttled applyConstraints — stays in
 * CameraScreen; this file is the part with answers a test can check.
 */

/** What the track says it can do, reduced to the two numbers the pinch needs. */
export interface ZoomRange {
  min: number;
  max: number;
}

/**
 * Where the pinch is silently unavailable, and why the button is not simply
 * missing a feature check:
 *
 * **Android inside the app has no camera zoom at all.** Not a permission to
 * ask for, not a constraint to name — Chromium's WebView does not implement
 * pan/tilt/zoom, so `getCapabilities()` omits `zoom` (it lists `torch`, which
 * is why the flash button is there) and `applyConstraints` throws
 * OverconstrainedError. Measured on the reporting phone: the same camera, the
 * same constraints, answered with `{min: 1, max: 8, step: 0.1}` in Chrome on
 * that phone and with nothing in the app's WebView. Asking for zoom in the
 * getUserMedia request — the documented way to obtain the PTZ permission — was
 * tried and changes nothing there. So on Android the pinch exists on the web
 * and not in the app, and that is the platform rather than this code.
 *
 * zoomRange returning null is what carries all of that: no range, no pinch
 * surface, no pill, exactly as a camera with no torch draws no flash button.
 */

/**
 * The track's zoom capability, or null where pinching would be a lie: no
 * capability at all, or a range too flat to move through — some cameras report
 * zoom with min equal to max, which is a capability in name only.
 */
export function zoomRange(track: MediaStreamTrack): ZoomRange | null {
  // `zoom` is not in lib.dom for the same reason `torch` is not: it is not in
  // the spec every browser implements. Asked of the track rather than the
  // browser, so the answer is about this camera.
  const capabilities = track.getCapabilities?.() as
    | { zoom?: { min?: number; max?: number } }
    | undefined;
  const zoom = capabilities?.zoom;
  if (!zoom || typeof zoom.min !== 'number' || typeof zoom.max !== 'number') return null;
  if (zoom.max - zoom.min < 0.1) return null;
  return { min: zoom.min, max: zoom.max };
}

/**
 * Where the track stands now — the number a fresh camera starts the pinch at,
 * held inside the range the same camera just advertised.
 *
 * The clamp is not defensive tidying. A phone with an ultra-wide lens opens the
 * back camera at `zoom: 0.667` while reporting `min: 1` — measured, not
 * imagined — and a pinch anchored on 0.667 against a floor of 1 spends its
 * first half computing numbers that clamp back to where they started: the
 * gesture reads as broken until the fingers are 50% further apart than they
 * began. It also lit the pill at «0,7×» over a camera nobody had touched.
 */
export function currentZoom(track: MediaStreamTrack, range: ZoomRange): number {
  const settings = track.getSettings?.() as { zoom?: number } | undefined;
  const zoom = typeof settings?.zoom === 'number' ? settings.zoom : 1;
  return Math.min(range.max, Math.max(range.min, zoom));
}

/**
 * The zoom a pinch has reached: the spread between the fingers, measured
 * against the spread the pinch began at, scales the zoom the pinch began at.
 * Anchored to the start rather than accumulated per frame, so a pinch that
 * wanders and comes back lands where it started instead of drifting.
 */
export function pinchZoom(
  startedAt: number,
  startSpread: number,
  spread: number,
  range: ZoomRange,
): number {
  if (startSpread <= 0) return startedAt;
  const next = startedAt * (spread / startSpread);
  return Math.min(range.max, Math.max(range.min, next));
}

/** «2,3×» — one decimal, the app's comma. */
export function formatZoom(zoom: number): string {
  return `${zoom.toFixed(1).replace('.', ',')}×`;
}

/**
 * Whether the number is worth a pill on the picture: 1× is where every camera
 * starts and saying so is noise. The tolerance swallows the not-quite-1 a
 * round trip through the track's own stepping can return.
 */
export function zoomWorthShowing(zoom: number): boolean {
  return Math.abs(zoom - 1) > 0.05;
}
